import axios from 'axios';
import { Client, MessageTemplate, SentMessage, User, ChatbotFlow } from '../types';

// Use environment variable or relative path in production (behind nginx)
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' 
    ? 'http://localhost:3001/api' 
    : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const clientApi = {
  getAll: async (): Promise<Client[]> => {
    const response = await api.get<Client[]>('/clients');
    return response.data;
  },

  getById: async (id: number): Promise<Client> => {
    const response = await api.get<Client>(`/clients/${id}`);
    return response.data;
  },

  create: async (client: Omit<Client, 'id' | 'created_at' | 'updated_at'>): Promise<Client> => {
    const response = await api.post<Client>('/clients', client);
    return response.data;
  },

  update: async (id: number, client: Partial<Client>): Promise<Client> => {
    const response = await api.put<Client>(`/clients/${id}`, client);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/clients/${id}`);
  },

  uploadCSV: async (csvContent: string): Promise<{ success: boolean; message: string; results: { success: number; errors: Array<{ row: number; error: string; data: any }>; total: number } }> => {
    const response = await api.post('/clients/upload-csv', { csvContent });
    return response.data;
  },
};

export const messageApi = {
  getTemplates: async (): Promise<MessageTemplate[]> => {
    const response = await api.get<MessageTemplate[]>('/messages/templates');
    return response.data;
  },

  getTemplate: async (type: string): Promise<MessageTemplate> => {
    const response = await api.get<MessageTemplate>(`/messages/templates/${type}`);
    return response.data;
  },

  updateTemplate: async (type: string, message: string): Promise<MessageTemplate> => {
    const response = await api.put<MessageTemplate>(`/messages/templates/${type}`, { message });
    return response.data;
  },

  sendTest: async (clientId: number, type: string): Promise<{ success: boolean; message: string; error?: string }> => {
    const response = await api.post<{ success: boolean; message: string; error?: string }>('/messages/send', {
      client_id: clientId,
      type,
    });
    return response.data;
  },

  getSentMessages: async (): Promise<SentMessage[]> => {
    const response = await api.get<SentMessage[]>('/messages/sent');
    return response.data;
  },

  getWhatsAppConfig: async (): Promise<{ serviceUrl: string; configured: boolean }> => {
    const response = await api.get('/messages/whatsapp/config');
    return response.data;
  },

  getWhatsAppStatus: async (): Promise<{ 
    connected: boolean; 
    serviceUrl?: string;
    status?: string;
    message?: string;
    error?: string;
    type?: string;
    client_status?: any;
  }> => {
    const response = await api.get('/messages/whatsapp/status', {
      timeout: 10000 // Aumentar timeout a 10 segundos
    });
    return response.data;
  },
};

export const authApi = {
  login: async (username: string, password: string): Promise<{ success: boolean; token: string; user: User }> => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  logout: async (): Promise<{ success: boolean }> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getMe: async (): Promise<{ success: boolean; user: User }> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  register: async (userData: { username: string; email: string; password: string; full_name?: string; role?: string }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
};

export const userApi = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/users');
    return response.data;
  },

  getById: async (id: number): Promise<User> => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },

  update: async (id: number, userData: Partial<User>): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

export const chatbotApi = {
  getAll: async (): Promise<ChatbotFlow[]> => {
    const response = await api.get<ChatbotFlow[]>('/chatbot');
    return response.data;
  },

  getById: async (id: number): Promise<ChatbotFlow> => {
    const response = await api.get<ChatbotFlow>(`/chatbot/${id}`);
    return response.data;
  },

  create: async (flow: Omit<ChatbotFlow, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; id: number; message: string }> => {
    const response = await api.post('/chatbot', flow);
    return response.data;
  },

  update: async (id: number, flow: Partial<ChatbotFlow>): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/chatbot/${id}`, flow);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/chatbot/${id}`);
    return response.data;
  },
};

export default api;

